import memoizeOne from 'memoize-one';
import React, { Component, Fragment } from 'react';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/SiMaxPrice';
import { DebounceInput } from '~/Debounce';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import ProductStatusIcon from '~/ProductStatusIcon';
import GeneralProduct from '~/Api/Product/Product';
import calculateComparativePrice from '~/SiMaxPrice/calculateComparativePrice';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Icon from '~/UI/Icon/Icon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import defaultSegmentedSelectStyles from '~/UI/SegmentedSelect/default.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';
import Tooltip from '~/UI/Tooltip';

import styles from './styles.less';

export type ReferencePriceValue = { value: string; parentValue: null; prompt: React.ReactNode } | null;
export type ReferencePriceInValues = {
	value: ReferencePriceValue;
	isValid: boolean;
};

export interface Values {
	referencePrice: ReferencePriceInValues;
	packageSizeForSingleUnit: {
		value: string;
		isValid: boolean;
	};
	packageSize: {
		value: string;
		isValid: boolean;
	};
	dosageStrength: {
		value: string;
		isValid: boolean;
	};
	selected: boolean;
}
export type OnChangeListener = (
	product: Product,
	referencePrice: ReferencePriceInValues | ReferencePriceValue | null,
	packageSize: string,
	packageSizeForSingleUnit: string,
	dosageStrength: string
) => void;

export type OnSelectListener = (product: Product, selected: boolean, range: boolean) => void;

interface Props {
	siMaxPriceProduct: Product;
	usedForCalculation: boolean;
	values: Values;
	referencePackageSize: number | null;
	referenceDosageStrength: number | null;
	disableSelection: boolean;
	highlightProduct: boolean;
	lastSelectedProduct: boolean;
	showReferenceDataSource: boolean;
	historicData: boolean;
	detailLink(product: { id: string }): { pathname: string; search: string };
	style: any;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	static defaultProps = {
		referencePackageSize: null,
		referenceDosageStrength: null,
		disableSelection: false,
		showReferenceDataSource: false,
		highlightProduct: null,
		lastSelectedProduct: null,
		style: null,
	};

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			JSON.stringify(this.props.siMaxPriceProduct) !== JSON.stringify(nextProps.siMaxPriceProduct) ||
			this.props.usedForCalculation !== nextProps.usedForCalculation ||
			this.props.values.packageSize.isValid !== nextProps.values.packageSize.isValid ||
			this.props.values.packageSize.value !== nextProps.values.packageSize.value ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.referencePrice.isValid !== nextProps.values.referencePrice.isValid ||
			this.props.values.referencePrice.value?.value !== nextProps.values.referencePrice.value?.value ||
			this.props.values.dosageStrength.value !== nextProps.values.dosageStrength.value ||
			this.props.values.dosageStrength.isValid !== nextProps.values.dosageStrength.isValid ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.referencePackageSize !== nextProps.referencePackageSize ||
			this.props.referenceDosageStrength !== nextProps.referenceDosageStrength ||
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.showReferenceDataSource !== nextProps.showReferenceDataSource ||
			this.props.historicData !== nextProps.historicData ||
			this.props.style !== nextProps.style ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.checked !== nextProps.checked
		);
	}

	resolveReferencePrice = memoizeOne((type: string | null, siMaxPriceProduct: Product): ReferencePrice | null => {
		if (type === null) {
			return null;
		}
		return (
			siMaxPriceProduct.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ?? null
		);
	});

	renderReferencePriceSelect(prompt: React.ReactNode) {
		const { siMaxPriceProduct, values, onChange } = this.props;

		return (
			<SegmentedSelect
				isValid={values.referencePrice.isValid}
				prompt={prompt}
				selectedOption={values.referencePrice.value}
				styles={defaultSegmentedSelectStyles}
				onSelect={(value) =>
					onChange(
						siMaxPriceProduct,
						value,
						values.packageSize.value,
						values.packageSizeForSingleUnit.value,
						values.dosageStrength.value
					)
				}
			>
				<SegmentedSelectPage>
					<SegmentedSelectSegment>
						{siMaxPriceProduct.referencePrices.map((price) => (
							<SegmentedSelectOption
								key={price.calculatedPrice.type}
								prompt={this.renderReferencePricePrompt(price)}
								value={price.calculatedPrice.type}
							>
								<div>{price.calculatedPrice.description}</div>
								<div>{price.calculatedPrice.price.roundedHumanFormat()} Comparative price</div>
								<div>
									<strong>
										{price.sourcePrice.price?.roundedHumanFormat() || 0} {price.sourcePrice.humanFormatType()}
									</strong>
								</div>
							</SegmentedSelectOption>
						))}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}

	renderReferencePricePrompt(price: ReferencePrice) {
		return (
			<abbr data-testid='cell-reference-price' title={price.calculatedPrice.description}>
				<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
				<div>
					<strong>{price.sourcePrice.price?.roundedHumanFormat() || 0}</strong>
				</div>
			</abbr>
		);
	}

	renderComparativePrice(referencePrice: ReferencePrice | null) {
		if (referencePrice === null) {
			return null;
		}

		const {
			values: { packageSize, dosageStrength },
			referencePackageSize,
			referenceDosageStrength,
		} = this.props;

		if (packageSize.isValid === false || (packageSize.isValid === null && packageSize.value !== '')) {
			return null;
		}
		if (dosageStrength.isValid === false || (dosageStrength.isValid === null && dosageStrength.value !== '')) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;
		const parsedDosageStrength =
			dosageStrength.value !== '' ? parseHumanFloat(dosageStrength.value) : referenceDosageStrength;
		if (parsedPackageSize === null || parsedDosageStrength === null) {
			return null;
		}

		return calculateComparativePrice(
			referencePrice.calculatedPrice.price,
			parsedPackageSize,
			parsedDosageStrength
		).roundedHumanFormat();
	}

	selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement>) => {
		const { siMaxPriceProduct, onSelect } = this.props;
		onSelect(siMaxPriceProduct, checked, event.nativeEvent.shiftKey);
	};

	render() {
		const {
			siMaxPriceProduct,
			usedForCalculation,
			values,
			referencePackageSize,
			referenceDosageStrength,
			disableSelection,
			highlightProduct,
			lastSelectedProduct,
			showReferenceDataSource,
			historicData,
			detailLink,
			style,
			checked,
			onChange,
			onSave,
			checkRow,
		} = this.props;
		const { product } = siMaxPriceProduct;
		const detailLinkString = detailLink(product);
		const referencePrice = this.resolveReferencePrice(values.referencePrice.value?.value || null, siMaxPriceProduct);
		const prompt = referencePrice ? this.renderReferencePricePrompt(referencePrice) : null;

		return (
			<div
				className={
					product.status.isRemoved() || product.status.isBeforeIntroduction()
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
				style={style}
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<div className={styles.countryIcon}>
							<CountryIcon code={product.dataset.source.country.alpha2} name={product.dataset.source.country.name} />
						</div>
						{product.dataset.source.country.alpha2}
						<abbr
							className={styles.datasetSourceShortName}
							title={`${product.dataset.source.shortName} — ${product.dataset.source.description}`}
						>
							{product.dataset.source.shortName}
						</abbr>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{siMaxPriceProduct.singleUnitPackageSize ? (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSizeForSingleUnit.value}
								onBlur={onSave}
								onChange={(value) =>
									onChange(
										siMaxPriceProduct,
										values.referencePrice.value,
										values.packageSize.value,
										value,
										values.dosageStrength.value
									)
								}
							>
								<Input
									id={`product-package-${product.id}`}
									isInvalid={values.packageSizeForSingleUnit.isValid === false}
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					) : (
						<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.referencePriceCell}>
					{siMaxPriceProduct.referencePrices.length > 1 ? (
						this.renderReferencePriceSelect(prompt)
					) : (
						<div className={styles.priceCellContent}>{prompt}</div>
					)}
					{siMaxPriceProduct.singleUnitPackageSize ? (
						<Tooltip
							position='right'
							reference={
								<span className={styles.exclamationMarkIcon}>
									<Icon name='red-exclamation-mark'>!</Icon>
								</span>
							}
							width={180}
						>
							Single unit package size
						</Tooltip>
					) : null}
				</div>
				<div className={styles.packageForCalculationCell}>
					<div className={styles.packageInput}>
						<DebounceInput
							value={values.packageSize.value}
							onBlur={onSave}
							onChange={(value) =>
								onChange(
									siMaxPriceProduct,
									values.referencePrice.value,
									value,
									values.packageSizeForSingleUnit.value,
									values.dosageStrength.value
								)
							}
						>
							<Input
								id={`product-package-for-calc-${product.id}`}
								isInvalid={values.packageSize.isValid === false}
								placeholder={String(referencePackageSize || '')}
								styles={inputCompactStyles}
							/>
						</DebounceInput>
					</div>
				</div>
				<div className={styles.packageForCalculationCell}>
					<div className={styles.packageInput}>
						<DebounceInput
							value={values.dosageStrength.value}
							onBlur={onSave}
							onChange={(value) =>
								onChange(
									siMaxPriceProduct,
									values.referencePrice.value,
									values.packageSize.value,
									values.packageSizeForSingleUnit.value,
									value
								)
							}
						>
							<Input
								id={`product-dosage-for-calc-${product.id}`}
								isInvalid={values.dosageStrength.isValid === false}
								placeholder={String(referenceDosageStrength || '')}
								styles={inputCompactStyles}
							/>
						</DebounceInput>
					</div>
				</div>
				<div className={styles.comparativePriceCell}>
					<Link
						data-testid='calculated-price'
						styles={{ button: styles.comparativePriceCellContent }}
						tabIndex={-1}
						to={detailLinkString}
					>
						{this.renderComparativePrice(referencePrice)}
					</Link>
				</div>
				<div className={styles.selectionCell}>
					<ProductStatusIcon
						historicData={historicData}
						referenceDataSource={showReferenceDataSource ? siMaxPriceProduct.referenceDataSource : undefined}
						status={product.status}
					/>
					<Checkbox
						disabled={disableSelection}
						focused={lastSelectedProduct}
						checked={!disableSelection && values.selected}
						id={`product-selection-${product.id}`}
						onChange={this.selectionChanged}
					/>
				</div>
				<div className={styles.usedForCalculationCell}>
					<Link styles={{ button: styles.usedForCalculationCellContent }} tabIndex={-1} to={detailLinkString}>
						{usedForCalculation ? <Icon name='formula' title='Used for calculation' /> : null}
					</Link>
				</div>
			</div>
		);
	}
}
